import { useFormik } from "formik";
import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  IQuestionDisplayProps,
  ISurveyAnswer,
} from "../../../../../../../@types/Surveys";
import { SliderInput } from "../../../../../../../components/Slider";
import { useDynamicsurveys } from "../../../../../../../hooks/useDynamicSurveys";
import {
  Title,
  Description,
} from "../TextQuestionDisplay/TextQuestionDisplay.styles";
import {
  HorizontalSliderContainer,
  verticalSliderCss,
  VerticalSliderContainer,
  LabelsRow,
  SliderContainer,
  LabelContainerLeft,
  LabelContainerRight,
  SliderValueHorizontal,
  SliderLabelHorizontal,
  SliderLabelVertical,
  SliderValueVertical,
  horizontalSliderCss,
  VerticalSliderInputContainer,
  FormAnswerField,
  verticalSliderCssNoTooltip,
  horizontalSliderCssNoTooltip,
  SliderUsabilityMessage,
} from "./SliderQuestionDisplay.styles";

interface ISliderProps extends IQuestionDisplayProps {
  verticalSliderHeight?: number | string;
}

export function SliderQuestionDisplay({
  title,
  description,
  options,
  step,
  orientation,
  questionId,
  verticalSliderHeight,
  isForms,
}: ISliderProps) {
  const [value, setValue] = useState<number>();
  const [renderSlider, setRenderSlider] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { t } = useTranslation();

  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const verticalContainerRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const handleSliderAreaTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      const sliderEl = e.currentTarget.querySelector(
        ".ant-slider"
      ) as HTMLElement;
      if (!sliderEl) return;

      e.stopPropagation();
      e.preventDefault();

      const sliderRect = sliderEl.getBoundingClientRect();
      const clamp = (clientX: number, clientY: number) => ({
        clientX: Math.max(sliderRect.left, Math.min(clientX, sliderRect.right)),
        clientY: Math.max(sliderRect.top, Math.min(clientY, sliderRect.bottom)),
      });

      const touch = e.touches[0];
      const clamped = clamp(touch.clientX, touch.clientY);

      sliderEl.dispatchEvent(
        new MouseEvent("mousedown", {
          clientX: clamped.clientX,
          clientY: clamped.clientY,
          bubbles: true,
          cancelable: true,
        })
      );

      const abortController = new AbortController();

      const onTouchMove = (ev: TouchEvent) => {
        ev.stopPropagation();
        ev.preventDefault();
        const t = ev.touches[0];
        const c = clamp(t.clientX, t.clientY);
        document.dispatchEvent(
          new MouseEvent("mousemove", {
            clientX: c.clientX,
            clientY: c.clientY,
            bubbles: true,
            cancelable: true,
          })
        );
      };

      const onTouchEnd = (ev: TouchEvent) => {
        ev.stopPropagation();
        ev.preventDefault();
        const t = ev.changedTouches[0];
        const c = clamp(t.clientX, t.clientY);
        document.dispatchEvent(
          new MouseEvent("mouseup", {
            clientX: c.clientX,
            clientY: c.clientY,
            bubbles: true,
            cancelable: true,
          })
        );
        abortController.abort();
        cleanupRef.current = null;
      };

      const listenerOptions = {
        passive: false,
        capture: true,
        signal: abortController.signal,
      };
      document.addEventListener("touchmove", onTouchMove, listenerOptions);
      document.addEventListener("touchend", onTouchEnd, listenerOptions);
      cleanupRef.current = () => abortController.abort();
    },
    []
  );

  const sliderFormik = useFormik({
    initialValues: {
      answer: 0,
    },
    onSubmit: () => {},
  });

  const { surveyAnswerArray, updateSurveyAnswerArray } = useDynamicsurveys();

  const updateAnswerArrayAndFormik = (value: number) => {
    const answerUpdate: ISurveyAnswer = {
      surveyQuestionId: questionId ?? "",
      answerValue: value.toString(),
    };
    updateSurveyAnswerArray(answerUpdate);
    sliderFormik.setFieldValue("answer", value);
  };

  useEffect(() => {
    const storedAnswer = surveyAnswerArray.find(
      (surveyAnswer) => surveyAnswer.surveyQuestionId === questionId
    );
    if (storedAnswer !== undefined) {
      const newValue = parseFloat(storedAnswer.answerValue);
      if (!Number.isNaN(newValue)) {
        setValue(newValue);
        updateAnswerArrayAndFormik(newValue);
        setShowTooltip(true);
      } else if (options) {
        setValue(options[0].value);
      }
    }
    setRenderSlider(true);
  }, []);

  const sliderUsabilityMessage = () => {
    return (
      <SliderUsabilityMessage>
        {t("survey_fixed_translations_survey_slider_message")}
      </SliderUsabilityMessage>
    );
  };

  if (options !== undefined && orientation !== undefined) {
    return (
      <FormAnswerField isForms={isForms}>
        <Title>{title}</Title>
        <Description>{description}</Description>
        {orientation === "Horizontal" && renderSlider && (
          <HorizontalSliderContainer>
            <SliderContainer
              ref={sliderContainerRef}
              onTouchStartCapture={handleSliderAreaTouchStart}
            >
              <SliderInput
                className={
                  showTooltip
                    ? horizontalSliderCss()
                    : horizontalSliderCssNoTooltip()
                }
                valueVisible={showTooltip}
                onValueChange={(value) => {
                  setValue(value);
                  if (!showTooltip) setShowTooltip(true);
                }}
                onAfterChange={(value) => {
                  setValue(value);
                  updateAnswerArrayAndFormik(value);
                  if (!showTooltip) setShowTooltip(true);
                }}
                min={options[0].value}
                max={options[1].value}
                step={step}
                defaultValue={value}
              />
            </SliderContainer>
            <LabelsRow>
              <LabelContainerLeft>
                <SliderValueHorizontal>
                  {options[0].value}
                </SliderValueHorizontal>
                <SliderLabelHorizontal>
                  {options[0].label}
                </SliderLabelHorizontal>
              </LabelContainerLeft>
              <LabelContainerRight>
                <SliderValueHorizontal>
                  {options[1].value}
                </SliderValueHorizontal>
                <SliderLabelHorizontal>
                  {options[1].label}
                </SliderLabelHorizontal>
              </LabelContainerRight>
            </LabelsRow>
            {sliderUsabilityMessage()}
          </HorizontalSliderContainer>
        )}
        {orientation === "Vertical" && renderSlider && (
          <VerticalSliderContainer
            ref={verticalContainerRef}
            onTouchStartCapture={handleSliderAreaTouchStart}
            style={{ height: verticalSliderHeight }}
          >
            <SliderValueVertical>{options[1].value}</SliderValueVertical>
            <SliderLabelVertical>{options[1].label}</SliderLabelVertical>
            <VerticalSliderInputContainer>
              <SliderInput
                valueVisible={showTooltip}
                onValueChange={(value) => {
                  setValue(value);
                  if (!showTooltip) setShowTooltip(true);
                }}
                onAfterChange={(value) => {
                  setValue(value);
                  updateAnswerArrayAndFormik(value);
                  if (!showTooltip) setShowTooltip(true);
                }}
                min={options[0].value}
                max={options[1].value}
                step={step}
                vertical
                className={
                  showTooltip
                    ? verticalSliderCss()
                    : verticalSliderCssNoTooltip()
                }
                defaultValue={value}
              />
            </VerticalSliderInputContainer>
            <SliderValueVertical>{options[0].value}</SliderValueVertical>
            <SliderLabelVertical>{options[0].label}</SliderLabelVertical>
            {sliderUsabilityMessage()}
          </VerticalSliderContainer>
        )}
      </FormAnswerField>
    );
  }
  return null;
}
